import React, { useRef, useState, useEffect } from 'react';
import { saveAs } from 'file-saver';

function FilesTab({
  folders,
  setFolders,
  newFolderName,
  setNewFolderName,
  handleCreateFolder,
  comments,
  handleDeleteComment,
  openFolderModal,
  replyTargetId,
  setReplyTargetId,
  replyContent,
  setReplyContent,
  handleReplySubmit,
  currentUser,
  isFullScreenFiles,
  openEditFolderModal,
  openDeleteFolderModal,
  handleDragMoveFolder,
  isProjectMaster
}) {
  const [replyFile, setReplyFile] = useState(null);
  const replyFileInputRef = useRef(null);

  // === 드래그 앤 드롭 상태 관리 ===
  const [draggedCommentId, setDraggedCommentId] = useState(null);
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const scrollContainerRef = useRef(null);
  const scrollIntervalRef = useRef(null);

  // === 아코디언 상태 관리 ===
  const [openFolders, setOpenFolders] = useState({});

  useEffect(() => {
    setOpenFolders(prev => {
      const newState = { ...prev };
      folders.forEach(f => {
        if (newState[f] === undefined) newState[f] = true;
      });
      return newState;
    });
  }, [folders]);

  const toggleFolder = (folder) => {
    setOpenFolders(prev => ({ ...prev, [folder]: !prev[folder] }));
  };

  const getFileIconClass = (fileName) => {
    if (!fileName) return 'fas fa-file-alt text-secondary';
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return 'fas fa-file-pdf text-danger';
      case 'doc': case 'docx': case 'rtf': return 'fas fa-file-word text-primary';
      case 'xls': case 'xlsx': case 'csv': return 'fas fa-file-excel text-success';
      case 'ppt': case 'pptx': case 'pps': return 'fas fa-file-powerpoint text-warning';
      case 'hwp': case 'hwpx': return 'fas fa-file-alt text-info';
      case 'jpg': case 'jpeg': case 'png': case 'gif': case 'svg': case 'webp': case 'bmp': case 'tiff': case 'ico': return 'fas fa-file-image text-success';
      case 'zip': case 'rar': case '7z': case 'tar': case 'gz': case 'alz': case 'egg': return 'fas fa-file-archive text-secondary';
      case 'mp4': case 'avi': case 'mkv': case 'mov': case 'wmv': case 'flv': case 'webm': return 'fas fa-file-video text-danger';
      case 'mp3': case 'wav': case 'ogg': case 'flac': case 'm4a': return 'fas fa-file-audio text-warning';
      case 'js': case 'json': case 'html': case 'css': case 'xml': case 'py': case 'java': case 'c': case 'cpp': case 'sql': case 'log': case 'sh': case 'php': return 'fas fa-file-code text-dark';
      case 'txt': case 'md': return 'fas fa-file-alt text-secondary';
      default: return 'fas fa-file text-muted';
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const pad = n => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const stripUniqueKey = (name) => {
    if (!name) return name;
    return name.replace(/_[0-9]{8}F[0-9]{6}(?=\.[^.]+$|$)/g, '');
  };

  const handleDownloadFile = async (fileObj) => {
    if (!fileObj) {
      alert('다운로드할 파일 정보가 없습니다.');
      return;
    }
    const downloadPath = fileObj.file_path || fileObj.path || (fileObj.file_name ? `/uploads/techni/pms/${fileObj.file_name}` : null) || (fileObj.originalName ? `/uploads/techni/pms/${fileObj.originalName}` : null);
    if (!downloadPath) {
      alert('다운로드할 파일 정보가 없습니다.');
      return;
    }
    try {
      const response = await fetch(downloadPath);
      const blob = await response.blob();
      saveAs(blob, stripUniqueKey(fileObj.file_name || 'download'));
    } catch (e) {
      console.error('다운로드 실패:', e);
      alert('파일 다운로드 중 오류가 발생했습니다.');
    }
  };

  const checkIsFile = (c) => {
    if (!c) return false;
    if (c.file || c.file_path || c.file_name) return true;
    return c.content && /\.(gif|jpg|jpeg|png|pdf|zip|doc|docx|xls|xlsx|ppt|pptx|txt|hwp|csv|ai|psd)$/i.test(c.content.trim());
  };

  // === 드래그 앤 드롭 이벤트 핸들러 ===
  const handleDragStart = (e, commentId) => {
    setDraggedCommentId(commentId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(commentId));
    setTimeout(() => {
      const el = document.getElementById(`file-node-${commentId}`);
      if (el) el.style.opacity = '0.4';
    }, 0);
  };

  const handleDragEnd = (e) => {
    if (e.currentTarget) e.currentTarget.style.opacity = '1';
    if (draggedCommentId) {
      const el = document.getElementById(`file-node-${draggedCommentId}`);
      if (el) el.style.opacity = '1';
    }
    document.querySelectorAll('[id^="file-node-"]').forEach(el => {
      el.style.opacity = '1';
    });
    setDraggedCommentId(null);
    setDragOverFolder(null);
    stopAutoScroll();
  };

  const handleFolderDragOver = (e, folderName) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverFolder(folderName);
    const container = scrollContainerRef.current;
    if (container) {
      const rect = container.getBoundingClientRect();
      const y = e.clientY;
      if (y < rect.top + 50) startAutoScroll(container, -5);
      else if (y > rect.bottom - 50) startAutoScroll(container, 5);
      else stopAutoScroll();
    }
  };

  const handleFolderDragLeave = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragOverFolder(null);
  };

  const handleFolderDrop = async (e, folderName) => {
    e.preventDefault();
    setDragOverFolder(null);
    stopAutoScroll();
    let droppedId = draggedCommentId;
    if (!droppedId) {
      const droppedIdStr = e.dataTransfer.getData('text/plain');
      if (droppedIdStr) droppedId = isNaN(Number(droppedIdStr)) ? droppedIdStr : Number(droppedIdStr);
    }
    if (droppedId && handleDragMoveFolder) {
      const el = document.getElementById(`file-node-${droppedId}`);
      if (el) el.style.opacity = '1';
      await handleDragMoveFolder(droppedId, folderName);
    }
    setDraggedCommentId(null);
  };

  const startAutoScroll = (container, speed) => {
    stopAutoScroll();
    scrollIntervalRef.current = setInterval(() => {
      container.scrollTop += speed;
    }, 16);
  };

  const stopAutoScroll = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  // === 답글 렌더링 ===
  const renderReplies = (parentId, depth = 1) => {
    const childReplies = (comments || []).filter(c => c.parent_id === parentId).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    if (childReplies.length === 0) return null;

    return childReplies.map(reply => {
      const isReplyFile = checkIsFile(reply);
      // 최상위 파일에 존재하는 그립 아이콘 공간(약 20px) 보정
      const indentPx = 36 + (depth * 16);

      return (
        <div key={reply.id} className="d-flex flex-column animation-fade-in">
          <div className="file-item-row d-flex align-items-center py-1 px-2 hover-bg-light rounded mb-1">
            <div style={{ width: `${indentPx}px`, minWidth: `${indentPx}px` }}></div>
            <i className="fas fa-level-up-alt fa-rotate-90 text-primary me-2 fs-8 opacity-50"></i>
            <i className={`${getFileIconClass(stripUniqueKey(reply.file?.originalName || reply.content))} fa-fw me-2 fs-6`}></i>

            {/* 좌측 밀착 자연스러운 흐름 배치 */}
            <div className="d-flex align-items-center flex-wrap">
              {isReplyFile ? (
                <>
                  <span className="file-link-hover fs-7 me-2 transition-colors" onClick={() => handleDownloadFile(reply.file)} title={`${stripUniqueKey(reply.content)} (클릭하여 다운로드)`}>{stripUniqueKey(reply.content)}</span>
                  <span className="badge bg-light text-secondary border fs-8 me-3 px-2 py-1" style={{ fontWeight: '500' }}>{Math.round((reply.file?.file_size || 1024) / 1024)} KB</span>
                </>
              ) : (
                <span className="text-dark fs-7 me-2" style={{ whiteSpace: 'pre-wrap' }}>{reply.content}</span>
              )}
              <span className="text-secondary d-flex align-items-center me-2" style={{ fontSize: '11px', fontWeight: '400' }}>
                <span className="opacity-75">{reply.user?.name || '알 수 없음'} {reply.user?.org?.position || reply.user?.position || ''}</span>
                <span className="text-muted ms-2 d-flex align-items-center">
                  <span className="opacity-50 mx-1">•</span>
                  {formatDateTime(reply.created_at)}
                </span>
              </span>
            </div>

            {/* 우측 끝으로 고정되는 액션 버튼들 */}
            <div className="d-flex align-items-center opacity-0 action-buttons transition-opacity ms-auto">
              {isReplyFile && (
                <button className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="다운로드" onClick={() => handleDownloadFile(reply.file)}>
                  <i className="fas fa-download"></i>
                </button>
              )}
              <button className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none ms-1 rounded" style={{ background: 'transparent' }} title="답글(파일첨부)" onClick={() => setReplyTargetId(reply.id)}>
                <i className="fas fa-plus"></i>
              </button>
              {(isProjectMaster || (currentUser && currentUser.name === reply.user?.name)) && (
                <button className="btn btn-sm text-danger hover-bg-gray p-1 shadow-none ms-1 rounded" style={{ background: 'transparent' }} title="삭제" onClick={() => handleDeleteComment(reply.id)}>
                  <i className="fas fa-trash-alt"></i>
                </button>
              )}
            </div>
          </div>

          {replyTargetId === reply.id && (
            <div className="mt-1 mb-2 d-flex align-items-center gap-2 animation-fade-in p-2 border-bottom" style={{ paddingLeft: `${indentPx + 24}px` }}>
              <div className="d-flex bg-white border flex-grow-1 align-items-center rounded" style={{ height: '30px' }}>
                <input type="text" className="form-control border-0 shadow-none h-100 flex-grow-1 rounded fs-8 px-2 bg-white"
                  placeholder={replyFile ? replyFile.name : "첨부할 파일을 선택하세요..."} readOnly onClick={() => replyFileInputRef.current?.click()} style={{ cursor: 'pointer' }} />
                <input type="file" className="d-none" ref={replyFileInputRef} onChange={(e) => setReplyFile(e.target.files[0])} />
                <button className="btn btn-sm bg-white border-start rounded-0 text-primary h-100 px-3 d-flex align-items-center justify-content-center" title="파일 첨부" onClick={() => replyFileInputRef.current?.click()}>
                  <i className="fas fa-upload"></i>
                </button>
              </div>
              <button className="btn btn-sm btn-primary rounded px-3 fw-bold shadow-sm" style={{ height: '30px', fontSize: '12px' }} onClick={() => { handleReplySubmit(reply.id, replyContent, replyFile); setReplyFile(null); }}>저장</button>
              <button className="btn btn-sm btn-secondary rounded px-3 fw-bold shadow-sm" style={{ height: '30px', fontSize: '12px' }} onClick={() => { setReplyTargetId(null); setReplyFile(null); }}>취소</button>
            </div>
          )}
          {renderReplies(reply.id, depth + 1)}
        </div>
      );
    });
  };

  // === 루트 코멘트 렌더링 ===
  const renderRootComment = (comment, isRootLevel = false) => {
    const isFile = checkIsFile(comment);

    return (
      <div key={comment.id} id={`file-node-${comment.id}`} className="d-flex flex-column transition-opacity" style={{ transition: 'opacity 0.2s' }}>
        <div className="file-item-row d-flex align-items-center py-2 px-2 hover-bg-light rounded position-relative mb-1">
          {!isRootLevel && <div style={{ width: '24px', minWidth: '24px' }}></div>}

          {/* 그립 아이콘 (수정본): 기본 투명, 호버 시에만 옅게 등장. 아이콘도 가로선으로 변경 */}
          <div className="text-muted d-flex align-items-center justify-content-center me-1 grip-icon transition-opacity"
            style={{ cursor: isFile ? 'grab' : 'default', width: '16px', opacity: 0 }}
            draggable={isFile}
            onDragStart={isFile ? (e) => handleDragStart(e, comment.id) : undefined}
            onDragEnd={isFile ? handleDragEnd : undefined}
            title={isFile ? '드래그하여 다른 폴더로 이동' : ''}>
            <i className="fas fa-grip-lines fs-7 opacity-50"></i>
          </div>

          <i className={`${isFile ? getFileIconClass(stripUniqueKey(comment.file?.originalName || comment.content)) : getFileIconClass(comment.content)} fa-fw me-2 fs-6`}></i>

          {/* 좌측 밀착 자연스러운 흐름 배치 */}
          <div className="d-flex align-items-center flex-wrap">
            {isFile ? (
              <>
                <span className="file-link-hover fs-7 me-2 transition-colors" onClick={() => handleDownloadFile(comment.file)} title={`${stripUniqueKey(comment.content)} (클릭하여 다운로드)`}>{stripUniqueKey(comment.content)}</span>
                <span className="badge bg-light text-secondary border fs-8 me-3 px-2 py-1" style={{ fontWeight: '500' }}>{Math.round((comment.file?.file_size || 1024) / 1024)} KB</span>
              </>
            ) : (
              <span className="text-dark fs-7 me-2" style={{ whiteSpace: 'pre-wrap' }}>{comment.content}</span>
            )}

            <span className="text-secondary opacity-75 me-2" style={{ fontSize: '11px', fontWeight: '400' }}>
              {comment.user?.name || '알 수 없음'} {comment.user?.org?.position || comment.user?.position || ''} <span className="opacity-50 mx-1">|</span> {formatDateTime(comment.created_at)}
            </span>
          </div>

          {/* 우측 끝으로 고정되는 액션 버튼들 */}
          <div className="d-flex align-items-center opacity-0 action-buttons transition-opacity ms-auto">
            {isFile && (
              <button className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="다운로드" onClick={() => handleDownloadFile(comment.file)}>
                <i className="fas fa-download"></i>
              </button>
            )}
            <button className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none ms-1 rounded" style={{ background: 'transparent' }} title="답글(파일첨부)" onClick={() => setReplyTargetId(comment.id)}>
              <i className="fas fa-plus"></i>
            </button>
            {(isProjectMaster || (currentUser && currentUser.name === comment.user?.name)) && (
              <button className="btn btn-sm text-danger hover-bg-gray p-1 shadow-none ms-1 rounded" style={{ background: 'transparent' }} title="삭제" onClick={() => handleDeleteComment(comment.id)}>
                <i className="fas fa-trash-alt"></i>
              </button>
            )}
            {isFile && (isProjectMaster || (currentUser && currentUser.name === comment.user?.name)) && (
              folders && folders.length === 0 ? (
                <span title="등록된 폴더가 존재하지 않습니다.">
                  <button className="btn btn-sm text-warning opacity-50 p-1 shadow-none ms-1 rounded" style={{ background: 'transparent' }} disabled><i className="far fa-folder"></i></button>
                </span>
              ) : (
                <button className="btn btn-sm text-warning hover-bg-gray p-1 shadow-none ms-1 rounded" style={{ background: 'transparent' }} title="폴더이동" onClick={() => openFolderModal(comment.id)}>
                  <i className="far fa-folder"></i>
                </button>
              )
            )}
          </div>
        </div>

        {replyTargetId === comment.id && (
          <div className="mt-1 mb-2 d-flex align-items-center gap-2 animation-fade-in p-2 border-bottom" style={{ paddingLeft: '48px' }}>
            <div className="d-flex bg-white border flex-grow-1 align-items-center rounded" style={{ height: '30px' }}>
              <input type="text" className="form-control border-0 shadow-none h-100 flex-grow-1 rounded fs-8 px-2 bg-white"
                placeholder={replyFile ? replyFile.name : "첨부할 파일을 선택하세요..."} readOnly onClick={() => replyFileInputRef.current?.click()} style={{ cursor: 'pointer' }} />
              <input type="file" className="d-none" ref={replyFileInputRef} onChange={(e) => setReplyFile(e.target.files[0])} />
              <button className="btn btn-sm bg-white border-start rounded-0 text-primary h-100 px-3 d-flex align-items-center justify-content-center" title="파일 첨부" onClick={() => replyFileInputRef.current?.click()}>
                <i className="fas fa-upload"></i>
              </button>
            </div>
            <button className="btn btn-sm btn-primary rounded px-3 fw-bold shadow-sm" style={{ height: '30px', fontSize: '12px' }} onClick={() => { handleReplySubmit(comment.id, replyContent, replyFile); setReplyFile(null); }}>저장</button>
            <button className="btn btn-sm btn-secondary rounded px-3 fw-bold shadow-sm" style={{ height: '30px', fontSize: '12px' }} onClick={() => { setReplyTargetId(null); setReplyFile(null); }}>취소</button>
          </div>
        )}

        {renderReplies(comment.id, isRootLevel ? 0 : 1)}
      </div>
    );
  };

  const rootComments = (comments || []).filter(c => c.parent_id === null);
  const fileRootComments = rootComments.filter(c => checkIsFile(c)).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const unassignedFiles = [];
  const filesByFolder = {};
  folders.forEach(f => { filesByFolder[f] = []; });

  fileRootComments.forEach(c => {
    if (c.folder_name && folders.includes(c.folder_name)) {
      filesByFolder[c.folder_name].push(c);
    } else {
      unassignedFiles.push(c);
    }
  });

  // 하위 항목(답글 등)까지 깊이 탐색하여 총 갯수를 구하는 함수
  const countDeepFiles = (rootArr) => {
    let total = 0;
    const countChildren = (parentId) => {
      const children = (comments || []).filter(c => c.parent_id === parentId);
      total += children.length;
      children.forEach(child => countChildren(child.id));
    };
    rootArr.forEach(c => {
      total += 1;
      countChildren(c.id);
    });
    return total;
  };

  return (
    <div className="animation-fade-in d-flex flex-column h-100 position-relative">
      <style>{`
        .hover-bg-light:hover { background-color: #f8f9fa !important; }
        .hover-bg-gray:hover { background-color: #e9ecef !important; }
        .file-item-row:hover .action-buttons, .folder-header:hover .folder-actions { opacity: 1 !important; }
        .file-item-row:hover .grip-icon { opacity: 1 !important; }
        .transition-opacity { transition: opacity 0.2s ease; }
        .transition-transform { transition: transform 0.2s ease; }
        .light-placeholder::placeholder { color: #adb5bd !important; opacity: 0.6 !important; }
        
        /* 파일명 전용 호버 스타일 */
        .file-link-hover { color: #343a40; font-weight: 500; text-decoration: none; cursor: pointer; }
        .file-link-hover:hover { color: #1c7ed6 !important; text-decoration: underline !important; }
      `}</style>

      {/* 상단 툴바: 심플하고 모던한 폴더 생성란 및 트리 컨트롤 */}
      {!isFullScreenFiles && (
        <div className="d-flex justify-content-between align-items-center mb-3 gap-2">
          {/* 전체 펼치기 / 접기 버튼 */}
          <div className="d-flex gap-2" style={{ transform: 'translateY(12px)' }}>
            <button className="btn btn-sm btn-outline-primary text-nowrap px-3 py-1 fs-8 fw-bold"
              onClick={() => {
                const allOpen = {};
                folders.forEach(f => allOpen[f] = true);
                setOpenFolders(allOpen);
              }} title="모든 폴더 펼치기">
              <i className="fas fa-angle-double-down me-2"></i>전체 펼치기
            </button>
            <button className="btn btn-sm btn-outline-primary text-nowrap px-3 py-1 fs-8 fw-bold"
              onClick={() => setOpenFolders({})} title="모든 폴더 접기">
              <i className="fas fa-angle-double-up me-2"></i>전체 접기
            </button>
          </div>

          <div className="d-flex border rounded bg-white shadow-sm align-items-center ps-3 pe-1 py-1" style={{ width: '100%', maxWidth: '300px' }}>
            <input type="text" className="form-control border-0 shadow-none p-0 fs-8 light-placeholder bg-transparent h-100"
              placeholder="새로운 폴더 이름 입력"
              value={newFolderName}
              onChange={e => setNewFolderName(e.target.value)}
              onKeyPress={e => { if (e.key === 'Enter' && newFolderName) { handleCreateFolder(newFolderName); setNewFolderName(''); } }} />
            <button className="btn btn-sm btn-primary rounded fw-bold px-3 ms-2 shadow-sm text-nowrap"
              onClick={() => { if (newFolderName) { handleCreateFolder(newFolderName); setNewFolderName(''); } }}>
              추가
            </button>
          </div>
        </div>
      )}

      {/* 퓨어 트리 뷰 영역 */}
      <div ref={scrollContainerRef} className="flex-grow-1 overflow-auto bg-white border rounded shadow-sm"
        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverFolder(''); }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverFolder(null); }}
        onDrop={(e) => handleFolderDrop(e, '')}>

        {dragOverFolder === '' && (
          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 100, pointerEvents: 'none' }}>
            <div className="bg-primary text-white px-4 py-2 rounded-pill shadow-lg fw-bold animation-fade-in fs-6">
              <i className="fas fa-reply fa-rotate-180 me-2"></i> 최상위로 이동
            </div>
          </div>
        )}

        {(fileRootComments.length === 0 && folders.length === 0) ? (
          <div className="d-flex flex-column align-items-center justify-content-center py-5 mt-4 opacity-75">
            <h6 className="fw-bold text-dark mb-1">등록된 파일이 없습니다</h6>
            <p className="text-muted fs-8 mb-0 text-center">폴더를 생성하여 파일들을 정리해 보세요!</p>
          </div>
        ) : (
          <div className="pb-3 w-100">
            {/* 1. 폴더 목록 */}
            {folders.map((folder, idx) => {
              const folderFiles = filesByFolder[folder] || [];
              const isOpen = openFolders[folder];

              return (
                <div key={idx} className="w-100"
                  style={{
                    backgroundColor: dragOverFolder === folder ? '#eef2ff' : 'transparent',
                    transition: 'background-color 0.2s ease'
                  }}
                  onDragOver={(e) => { e.stopPropagation(); handleFolderDragOver(e, folder); }}
                  onDragLeave={handleFolderDragLeave}
                  onDrop={(e) => { e.stopPropagation(); handleFolderDrop(e, folder); }}>

                  {/* 폴더 헤더 */}
                  <div className="d-flex align-items-center py-2 px-2 folder-header border-bottom hover-bg-light"
                    style={{ cursor: folderFiles.length > 0 ? 'pointer' : 'default' }}
                    onClick={() => { if (folderFiles.length > 0) toggleFolder(folder); }}>

                    <div style={{ width: '20px', textAlign: 'center' }}>
                      <i className={`fas fa-angle-${isOpen ? 'down' : 'right'} text-secondary fs-6 transition-transform`} style={{ opacity: folderFiles.length === 0 ? 0.25 : 1 }}></i>
                    </div>
                    <i className="fas fa-folder text-warning mx-2 fs-5 transition-opacity" style={{ color: '#fcc419', opacity: folderFiles.length === 0 ? 0.4 : 1 }}></i>

                    {/* 폴더명 및 파일 갯수 뱃지 */}
                    <span className={`fs-7 fw-bold transition-opacity ${folderFiles.length === 0 ? 'text-secondary opacity-50' : 'text-dark'}`}>{folder}</span>
                    <span className="badge rounded-pill ms-2" style={{ backgroundColor: '#e9ecef', color: '#6c757d', fontSize: '11px', padding: '3px 7px' }}>
                      {countDeepFiles(folderFiles)}
                    </span>

                    {/* 우측 끝으로 고정되는 폴더 액션 버튼 */}
                    <div className="d-flex gap-1 folder-actions opacity-0 transition-opacity ms-auto" onClick={e => e.stopPropagation()}>
                      <button className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="폴더명 변경" onClick={() => openEditFolderModal(folder)}>
                        <i className="fas fa-edit"></i>
                      </button>
                      <button className="btn btn-sm text-danger hover-bg-gray p-1 shadow-none rounded" style={{ background: 'transparent' }} title="폴더 삭제" onClick={() => openDeleteFolderModal(folder)}>
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    </div>
                  </div>

                  {/* 폴더 내부 파일 목록 */}
                  {isOpen && folderFiles.length > 0 && (
                    <div className="w-100">
                      {folderFiles.map((comment, i) => renderRootComment(comment))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* 2. 소속 없는 최상위 파일들 */}
            {unassignedFiles.length > 0 && (
              <div className="w-100">
                {unassignedFiles.map((comment) => renderRootComment(comment, true))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default FilesTab;
